import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Slot } from "radix-ui"
import { Spinner } from "@/components/ui/spinner"

/**
 * 控件尺度照搬搬小书 `xhshub/apps/web/src/components/ui/button.tsx`：
 * 默认 44pt 高、10px 圆角、16px 字号、按下缩放 0.97 —— 接近原生按钮的点击区与手感。
 * 颜色仍是本站的黑白灰 token。
 */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-control border border-transparent text-callout font-medium outline-none transition-[color,background-color,transform,brightness] duration-150 select-none focus-visible:ring-2 focus-visible:ring-ring/40 active:scale-[0.97] active:brightness-95 active:duration-0 disabled:pointer-events-none disabled:bg-muted disabled:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 ios:rounded-[14px] ios:font-semibold ios:[&_svg:not([class*='size-'])]:size-[18px]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground hover:bg-accent ios:bg-card ios:in-data-[slot=card]:bg-secondary",
        /** 浅底主色按钮（原 tinted） */
        tinted: "bg-primary/10 text-primary hover:bg-primary/15",
        outline: "border-border bg-background text-foreground hover:bg-muted ios:border-transparent ios:bg-card ios:in-data-[slot=card]:bg-secondary",
        ghost: "text-primary hover:bg-muted",
        destructive: "bg-destructive text-primary-foreground hover:bg-destructive/90",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-4 ios:h-[50px] ios:text-body",
        sm: "h-9 px-3 text-subheadline",
        lg: "h-12 px-5",
        icon: "size-9 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** 进行中：显示 spinner 并禁用，避免「点了没反馈」 */
    loading?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {/* asChild 走 Slot，children 必须**恰好一个**元素（null 也算），
          所以 loading 的 Spinner 只在普通 button 分支里包进 Fragment。 */}
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Spinner /> : null}
          {children}
        </>
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
